/* eslint-disable @typescript-eslint/no-explicit-any */
import { Observable } from "rxjs";
import { mapTo } from "rxjs/operators";
import type {
    RemoveArguments,
    UploadArguments,
} from "src/containers/household-claim/types";
import { inject, injectable } from "tsyringe";

import { filenameToMime } from "src/libs/filename-to-mime";
import { Log } from "src/libs/log";
import { HouseholdClaimEndpoints } from "src/containers/household-claim/constants/endpoints";
import { HouseholdClaimCache } from "src/containers/household-claim/models/claim-cache.model";
import { HouseholdClaimBooking } from "src/containers/household-claim/models/household-claim-booking.model";
import { autoBind } from "src/shared/decorators/autobind";
import { HttpClient } from "src/infra/http/client";
import { GenericSuccessResponse } from "src/infra/http/types";
import { replaceString } from "src/libs/replace-string";

type BaseOptions = Record<string, any> | undefined;
type ArgRequestOptions = {
    userIdentity: string;
    params: BaseOptions;
    data: BaseOptions;
    headers: BaseOptions;
};

@injectable()
export class HouseholdClaimRepository {
    constructor(@inject("HttpClient") private http: HttpClient) {}

    @autoBind
    private getRequestOptions({
        userIdentity = window.userIdentity,
        params = undefined,
        data = undefined,
        headers = undefined,
    }: Partial<ArgRequestOptions>) {
        const options: Record<string, any> = {
            isPublicRequest: false,
            host: process.env.REACT_APP_APIMGMT_URL,
            customHeader: {
                ...this.http.httpUtils.getBaseHeaders(),
                ...this.http.httpUtils.getMsBaseHeaders(userIdentity, "MEP"),
            },
        };
        if (params) {
            options.params = params;
        }
        if (data) {
            options.data = data;
        }
        if (headers) {
            options.customHeader = {
                ...options.customHeader,
                ...headers,
            };
        }
        return options;
    }

    @autoBind
    getClaims(): Observable<object[]> {
        const endpoint = HouseholdClaimEndpoints.GetClaims;

        return this.http.get<Record<string, any>[]>(
            endpoint,
            this.getRequestOptions({})
        );
    }

    @autoBind
    getClaim(claimId: number): Observable<object> {
        const endpoint = replaceString(HouseholdClaimEndpoints.GetClaim, {
            ":claimId": claimId.toString(),
        });
        return (
            this.http
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                .get<Record<string, any>>(endpoint, this.getRequestOptions({}))
        );
    }

    @autoBind
    deleteClaim(claimId: number): Observable<GenericSuccessResponse> {
        const baseEndpoint = HouseholdClaimEndpoints.DeleteClaim;
        const endpoint = replaceString(baseEndpoint, {
            ":claimId": claimId.toString(),
        });
        return this.http
            .delete<GenericSuccessResponse>(
                endpoint,
                this.getRequestOptions({})
            )
            .pipe(mapTo({ code: 200, status: "ok" }));
    }

    @autoBind
    bookClaim(
        userIdentity: string,
        booking: HouseholdClaimBooking
    ): Observable<object> {
        const endpoint = HouseholdClaimEndpoints.CreateHouseholdBook;
        return this.http.post<Record<string, any>>(
            endpoint,
            this.getRequestOptions({ userIdentity, data: booking.toJson() })
        );
    }

    @autoBind
    getDocumentsTreeByCoverage(
        userIdentity: string,
        coverageIds: number[],
        productCode: string,
        claimId: string
    ): Observable<object[]> {
        const endpoint = HouseholdClaimEndpoints.GetDocumentsTree;
        const params = {
            coverageIds: coverageIds.join(),
            productCode,
            claimId,
        };
        return this.http.get<Record<string, any>[]>(
            endpoint,
            this.getRequestOptions({ userIdentity, params })
        );
    }

    @autoBind
    getDecisionTree(): Observable<object[]> {
        const endpoint = HouseholdClaimEndpoints.GetDecisionTree;
        return this.http.get<Record<string, any>[]>(
            endpoint,
            this.getRequestOptions({})
        );
    }

    @autoBind
    getCache(userIdentity: string, claimId: number): Observable<object> {
        const endpoint = HouseholdClaimEndpoints.CACHE.replace(
            ":claimId",
            claimId.toString()
        );
        return this.http.get<Record<string, any>>(
            endpoint,
            this.getRequestOptions({ userIdentity })
        );
    }

    @autoBind
    saveCache(
        userIdentity: string,
        cache: HouseholdClaimCache
    ): Observable<object> {
        const endpoint = HouseholdClaimEndpoints.CACHE.replace(
            ":claimId",
            cache.id.toString()
        );
        return this.http.put<GenericSuccessResponse>(
            endpoint,
            this.getRequestOptions({ userIdentity, data: cache.toJson() })
        );
    }

    @autoBind
    uploadDocument(args: UploadArguments): Observable<object> {
        const data = new FormData();
        data.append("file", args.file);
        data.append("coverageIds", args.coverageIds);
        data.append("type", args.category);
        data.append("assetId", args.assetId as string);
        const endpoint = replaceString(HouseholdClaimEndpoints.UploadDocument, {
            ":category": "hogar",
            ":claimId": args.claimId,
        });
        return this.http.post<object>(
            endpoint,
            this.getRequestOptions({
                userIdentity: args.userIdentity,
                data,
                headers: { "Content-Type": "multipart/form-data" },
            })
        );
    }

    @autoBind
    removeDocument(args: RemoveArguments): Observable<GenericSuccessResponse> {
        const endpoint = replaceString(HouseholdClaimEndpoints.RemoveDocument, {
            ":category": "hogar",
            ":claimId": args.claimId,
            ":fileId": args.fileId,
        });
        return this.http.delete<GenericSuccessResponse>(
            endpoint,
            this.getRequestOptions({ userIdentity: args.userIdentity })
        );
    }

    @autoBind
    getAdjusters(): Observable<object[]> {
        const endpoint = HouseholdClaimEndpoints.GetAdjusters;
        return this.http.get<Record<string, any>[]>(
            endpoint,
            this.getRequestOptions({})
        );
    }

    @autoBind
    getTrackingDigital(): Observable<object[]> {
        const endpoint = HouseholdClaimEndpoints.TrackingDigital;
        return this.http.get<Record<string, any>[]>(
            endpoint,
            this.getRequestOptions({})
        );
    }

    @autoBind
    async fetchDocument(url: string, filename: string): Promise<File> {
        try {
            const response = await fetch(url);
            const blob = await response.blob();
            const mime = filenameToMime(filename);
            return new File([blob], filename, { type: mime });
        } catch (e) {
            Log.info("Ha ocurrido un error al descargar el documento");
            throw e;
        }
    }

    @autoBind
    getBookingAssets(coverageIds: string[]): Observable<object[]> {
        const endpoint = HouseholdClaimEndpoints.GetAssets;
        const params = {
            coverageIds: coverageIds.join(),
        };
        return this.http.get<Record<string, any>[]>(
            endpoint,
            this.getRequestOptions({ params })
        );
    }
}
